import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  deleteSpecClaimProposals, deleteSpecClauseProposal,
  deleteSpecClauseTerm, downloadSpecDocx, getSpecExportPreflight,
  getSpecClauses, getSpecTerms,
  postSpecClaimAccept, postSpecClaimEnable, postSpecClaimQc,
  postSpecClauseAccept, postSpecClauseTerms,
  postSpecClauseMerge, postSpecClauseSplit, postSpecClausesResplit,
  postSpecEnableAllClaims, getJobStatus,
  SpecClaimClause, SpecClaimQcFinding, SpecClauseEnableResult,
  SpecTermWebEntry,
} from '@/api/client';
import AppFooter from '@/components/layout/AppFooter';
import CoursesLink from '@/components/layout/CoursesLink';
import { describeApiError, DescribedApiError } from '@/lib/describeApiError';
import { broadcastDdAccepted } from './ddAcceptedBroadcast';
import { humaniseExportWarning } from './BundleExportModal';

/**
 * Description Workbench — Dhruva's claim-by-claim DOE enablement page
 * (docs/DETAILED_DESCRIPTION_WORKBENCH_PLAN.md DD-4).
 *
 * The frozen claim set is segmented into clauses (DD-1). Under each
 * clause the drafter cursor-selects terms/phrases; each selection is
 * anchored to the clause on the server the moment it is made, so every
 * way of generating the claim defines the same terms. "Generate
 * specification" writes the claim's blocks — for each clause: the
 * clause restated, one definition per selected term, the HOW→
 * RELATIONSHIP→WHY→TECHNICAL EFFECT paragraph, and the claim benefit
 * on the last clause — and shows them HERE for review. "QC claim"
 * grades that text before anything is written; Accept puts it into
 * the detailed-description section (DD-3). A drafter reported the
 * previous straight-to-description behaviour as confusing — the text
 * existed but the only way to read it was to leave this page and go
 * looking for it. Two modes: claim-by-claim (the drafter preference)
 * or Enable-all, which starts ONE server-side job and polls it — the
 * run is dozens of LLM calls and outlives the request, so looping it
 * client-side died at the proxy timeout.
 *
 * Native HTML + Tailwind, same pattern as UsersPage / Settings.
 */

interface Props {
  matterId: string;
  onBack?: () => void;
  /** The user-guide videos (the Courses link). */
  onOpenCourses?: () => void;
}

/** In-flight edits to one clause's proposed text. */
type ClauseEdits = {
  clause_restatement?: string;
  clause_paragraph?: string;
  claim_benefit_paragraph?: string;
  /** definition paragraph, keyed by term. */
  definitions?: Record<string, string>;
};

/** Turn the drafter's edits into the accept payload. Fields they did
 * not touch are omitted so the generated wording is used. */
function acceptPayload(
  proposal: SpecClauseEnableResult | undefined,
  edit: ClauseEdits | undefined,
) {
  if (!proposal || !edit) return {};
  const out: {
    clause_restatement?: string;
    clause_paragraph?: string;
    claim_benefit_paragraph?: string;
    definitions?: { term: string; paragraph: string }[];
  } = {};
  if (typeof edit.clause_restatement === 'string') {
    out.clause_restatement = edit.clause_restatement;
  }
  if (typeof edit.clause_paragraph === 'string') {
    out.clause_paragraph = edit.clause_paragraph;
  }
  if (typeof edit.claim_benefit_paragraph === 'string') {
    out.claim_benefit_paragraph = edit.claim_benefit_paragraph;
  }
  const defs = edit.definitions || {};
  const touched = (proposal.definitions || [])
    .filter((d) => typeof defs[d.term] === 'string')
    .map((d) => ({ term: d.term, paragraph: defs[d.term] }));
  if (touched.length) out.definitions = touched;
  return out;
}

/** Blob → text. ``Blob.text()`` is missing in older engines (and in
 * jsdom), so fall back to a FileReader. */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof (blob as { text?: unknown }).text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

const outlineBtn =
  'inline-flex h-8 items-center justify-center rounded border border-input ' +
  'bg-background px-2 text-xs font-medium hover:bg-accent disabled:opacity-50';
const primaryBtn =
  'inline-flex h-8 items-center justify-center rounded-md bg-primary px-3 ' +
  'text-xs font-medium text-primary-foreground hover:bg-primary/90 ' +
  'disabled:opacity-50';

/** One headline per failure kind, so a cost cap never again reads
 * the same as a provider outage or a dropped connection. */
const ERROR_HEADLINE: Record<string, string> = {
  cost_cap: 'AI spend cap reached',
  timeout: 'The request timed out',
  network: 'Could not reach the server',
  provider: 'The AI provider is unavailable',
  validation: 'That request was rejected',
  not_found: 'Not found',
  conflict: 'Conflicting change',
  permission: 'Not permitted',
  unknown: 'Something went wrong',
};

/** Poll cadence for the matter-wide enable job. A full run is
 * minutes of LLM work, so a slow poll is plenty and keeps the
 * request log quiet. */
const ENABLE_ALL_POLL_MS = 2500;

/** How often to re-read the clause list while a claim is generating.
 * Each clause is minutes of model time, so this is only about showing
 * finished clauses promptly, not about being quick. */
const GENERATING_POLL_MS = 5000;

/** Terminal job statuses. The endpoint returns the durable
 * stage_jobs vocabulary for module jobs and RQ's native one for
 * legacy ones, so both have to be recognised — checking only RQ's
 * 'finished' would poll a completed spec job forever. */
const JOB_DONE = new Set(['succeeded', 'finished']);
const JOB_DEAD = new Set([
  'failed', 'stopped', 'cancelled', 'refused_all_providers',
]);
/** Stop polling eventually. A worker that dies without marking the
 * row terminal leaves the status on 'running' forever, and the
 * status endpoint returns 'unknown' for a durable row it cannot
 * classify — neither is in DONE or DEAD, so without a ceiling the
 * browser would poll until the tab closed. 40 minutes is well past
 * a real matter-wide run. */
const ENABLE_ALL_MAX_POLLS = Math.ceil((40 * 60 * 1000) / 2500);

/**
 * The enable-all job "succeeds" as a JOB whenever the loop ran to its
 * own conclusion — including when it stopped at the spend cap or some
 * claims failed. Those outcomes live in the RESULT, not the status,
 * and the first cut of the poll loop broke on status alone and showed
 * a clean finish over claims left undone (2026-09-02 review). The
 * status endpoint returns the full result on the RQ branch and a
 * flattened summary (`{_list_len}` for lists) on the durable branch;
 * both are handled.
 */
function describeEnableAllResult(result: unknown): DescribedApiError | null {
  if (!result || typeof result !== 'object') return null;
  const r = result as Record<string, any>;
  const listLen = (v: unknown): number =>
    Array.isArray(v) ? v.length
      : (v && typeof v === 'object' && typeof (v as any)._list_len === 'number')
        ? (v as any)._list_len : 0;
  const claimNums = (v: unknown): string =>
    Array.isArray(v)
      ? v.map((c) => c?.claim_number).filter((n) => n != null).join(', ')
      : '';
  if (r.status === 'stopped_at_cost_cap') {
    const done = listLen(r.claims_enabled);
    return {
      kind: 'cost_cap', retryable: false, code: 'spec_cost_cap_exceeded',
      message: `Stopped at claim ${r.stopped_at_claim ?? '?'} — the `
        + `matter's AI spend cap was reached. ${done} claim`
        + `${done === 1 ? '' : 's'} before it ${done === 1 ? 'is' : 'are'} `
        + 'enabled and saved; the rest will need another run once the '
        + 'cap resets.',
    };
  }
  if (r.status === 'locked') {
    return {
      kind: 'conflict', retryable: false,
      message: 'The specification is locked (drawings are no longer '
        + 'frozen), so nothing was enabled.',
    };
  }
  const failed = listLen(r.claims_failed);
  if (failed > 0) {
    const which = claimNums(r.claims_failed);
    return {
      kind: 'provider', retryable: true, code: 'dd_enable_partial',
      message: `${failed} claim${failed === 1 ? '' : 's'} could not be `
        + `enabled${which ? ` (${which})` : ''}. The others are saved — `
        + 'retry the failed ones from the claim rail.',
    };
  }
  return null;
}

const SEVERITY_CLASS: Record<string, string> = {
  blocker: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-zinc-50 border-zinc-200 text-zinc-600',
};

function claimStatus(clauses: SpecClaimClause[]): 'none' | 'partial' | 'done' {
  const enabled = clauses.filter((c) => c.status === 'enabled').length;
  if (enabled === 0) return 'none';
  if (enabled === clauses.length) return 'done';
  return 'partial';
}

export default function DescriptionWorkbench({ matterId, onBack, onOpenCourses }: Props) {
  const [clauses, setClauses] = useState<SpecClaimClause[]>([]);
  const [terms, setTerms] = useState<SpecTermWebEntry[]>([]);
  const [loading, setLoading] = useState(true);
  // Structured, not a bare string: a cost cap, an edge timeout and a
  // provider outage used to collapse into the same "Request failed."
  const [err, setErr] = useState<DescribedApiError | null>(null);
  const [resplitNote, setResplitNote] = useState<number[]>([]);
  // What the last Accept wrote, so the drafter is told where it went
  // instead of wondering why this page looks the same.
  const [acceptedNote, setAcceptedNote] = useState<string | null>(null);
  // What the last export said — the file name and every pre-export or
  // export-time warning, which this path would otherwise swallow.
  const [exportNote, setExportNote] = useState<string | null>(null);
  // Bumped on every term add/remove. A clause-list poll that started
  // before the change must not put the old term list back.
  const termsVersion = useRef(0);
  // Busy state: 'claim-3' | 'clause-<id>' | 'qc-3' | 'all' | 'export'.
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  // Latest enable results by clause id (transient, richer than the
  // reload payload: carries definition_gaps + flags).
  const [lastResults, setLastResults] =
    useState<Record<string, SpecClauseEnableResult>>({});
  // The drafter's edits to proposed text, keyed by clause id, then by
  // field (definitions by term). Held here rather than pushed to the
  // server on every keystroke: Accept sends whatever is in the fields.
  const [edits, setEdits] = useState<Record<string, ClauseEdits>>({});
  // Claims the server is writing right now. A claim is minutes of
  // model time per clause, so generation runs server-side and the
  // proposals appear here as they land.
  const [generating, setGenerating] = useState<number[]>([]);
  const [qcByClaim, setQcByClaim] = useState<Record<number, {
    findings: SpecClaimQcFinding[];
    summary: { pass: boolean; blockers: number; warnings: number; info: number };
  }>>({});
  const [activeClaim, setActiveClaim] = useState<number | null>(null);
  const clauseRefs = useRef<Record<string, HTMLElement | null>>({});

  const load = useCallback(async () => {
    setErr(null);
    const termsAtStart = termsVersion.current;
    try {
      const [cl, tw] = await Promise.all([
        getSpecClauses(matterId),
        getSpecTerms(matterId),
      ]);
      setClauses(cl.clauses);
      setResplitNote(cl.resplit_claims || []);
      setGenerating(cl.generating_claims || []);
      // A term was added or removed while this was in flight: the
      // list in hand predates it, so keep the newer one.
      if (termsVersion.current === termsAtStart) setTerms(tw.terms);
    } catch (e: unknown) {
      setErr(describeApiError(e));
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  // While the server is writing a claim, re-read the clause list so
  // each finished clause shows up as soon as it is written instead of
  // after one long silence.
  useEffect(() => {
    if (generating.length === 0) return undefined;
    const t = setInterval(() => { void load(); }, GENERATING_POLL_MS);
    return () => clearInterval(t);
  }, [generating, load]);

  const byClaim = useMemo(() => {
    const m = new Map<number, SpecClaimClause[]>();
    for (const c of clauses) {
      const arr = m.get(c.claim_number) || [];
      arr.push(c);
      m.set(c.claim_number, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.clause_index - b.clause_index);
    }
    return m;
  }, [clauses]);
  const claimNumbers = useMemo(
    () => [...byClaim.keys()].sort((a, b) => a - b), [byClaim],
  );
  // Something is in the Detailed Description, so there is something
  // to export.
  const anyAccepted = useMemo(
    () => clauses.some((c) => c.status === 'enabled'), [clauses],
  );

  const anchoredTermsFor = useCallback(
    (claimNumber: number, clauseIndex: number) =>
      terms.filter(
        (t) => t.claim_number === claimNumber
          && t.clause_index === clauseIndex && t.status === 'selected',
      ),
    [terms],
  );

  /** Capture the drafter's current text selection when it lives
   * inside the given clause card. */
  const captureSelection = (clauseId: string): string | null => {
    const sel = window.getSelection();
    const text = (sel?.toString() || '').trim();
    if (!text) return null;
    const host = clauseRefs.current[clauseId];
    if (host && sel?.anchorNode && !host.contains(sel.anchorNode)) {
      return null;
    }
    return text.length > 120 ? null : text;
  };

  /** Replace one clause's anchored terms with the server's answer. */
  const applyClauseTerms = (
    claimNumber: number, clauseIndex: number, next: SpecTermWebEntry[],
  ) => {
    termsVersion.current += 1;
    setTerms((prev) => [
      ...prev.filter(
        (t) => !(t.claim_number === claimNumber && t.clause_index === clauseIndex
          && t.status === 'selected'),
      ),
      ...next,
    ]);
  };

  /** Anchor the selection to the clause on the server right away.
   * The selections used to wait in this page's memory for a per-clause
   * enable that no longer exists, so "Generate specification" never
   * received them and the terms the drafter picked were not defined. */
  const addSelection = (clause: SpecClaimClause, kind: 'term' | 'phrase') => {
    const text = captureSelection(clause.id);
    if (!text) return;
    window.getSelection()?.removeAllRanges();
    const already = anchoredTermsFor(clause.claim_number, clause.clause_index)
      .some((t) => t.term_text.toLowerCase() === text.toLowerCase());
    if (already) return;
    void run(`term-${clause.id}`, async () => {
      const res = await postSpecClauseTerms(
        matterId, clause.claim_number, clause.clause_index,
        [{ text, kind }],
      );
      applyClauseTerms(clause.claim_number, clause.clause_index, res.terms);
    });
  };

  const removeSelection = (clause: SpecClaimClause, term: SpecTermWebEntry) =>
    run(`term-${clause.id}`, async () => {
      await deleteSpecClauseTerm(
        matterId, clause.claim_number, clause.clause_index, term.id,
      );
      termsVersion.current += 1;
      setTerms((prev) => prev.filter((t) => t.id !== term.id));
    });

  /** Tell the Spec page (usually open in the tab this one was opened
   * from) that the Detailed Description changed. */
  const broadcastAccepted = (claimNumber: number) =>
    broadcastDdAccepted(matterId, claimNumber);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setErr(null);
    try {
      await fn();
    } catch (e: unknown) {
      setErr(describeApiError(e));
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  /**
   * Poll a queued enable job to its end. Returns the job's result (the
   * same shape the inline call returns) or throws on a dead job.
   * Shared by all three enable buttons: a single clause measured at up
   * to 266s on a thinking model, so every enable is job-backed when the
   * async path is on and the request must not be the thing we wait on.
   */
  const awaitEnableJob = async (jobId: string): Promise<unknown> => {
    let polls = 0;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, ENABLE_ALL_POLL_MS));
      // eslint-disable-next-line no-await-in-loop
      const s = await getJobStatus(jobId);
      if (s.progress?.message) setProgress(s.progress.message);
      if (JOB_DONE.has(s.status)) return s.result;
      if (JOB_DEAD.has(s.status)) {
        throw new Error(s.error || 'The enable run did not finish.');
      }
      polls += 1;
      if (polls >= ENABLE_ALL_MAX_POLLS) {
        throw new Error(
          'Still running after 40 minutes — this page has stopped '
          + 'watching. Reload to see the clauses enabled so far.',
        );
      }
    }
  };

  const acceptClause = (clause: SpecClaimClause) =>
    run(`clause-${clause.id}`, async () => {
      await postSpecClauseAccept(
        matterId, clause.claim_number, clause.clause_index,
        acceptPayload(
          clause.proposed || lastResults[clause.id], edits[clause.id],
        ),
      );
      setEdits((e) => {
        const next = { ...e };
        delete next[clause.id];
        return next;
      });
      // The paragraphs now exist, so drop the local copy of the
      // generate result and let the reload show the linked bodies.
      setLastResults((r) => {
        const next = { ...r };
        delete next[clause.id];
        return next;
      });
      setAcceptedNote(
        `Clause ${clause.clause_index + 1} of claim ${clause.claim_number} `
        + 'is now in the Detailed Description.',
      );
      broadcastAccepted(clause.claim_number);
      await load();
    });

  const discardProposal = (clause: SpecClaimClause) =>
    run(`clause-${clause.id}`, async () => {
      await deleteSpecClauseProposal(
        matterId, clause.claim_number, clause.clause_index,
      );
      setEdits((e) => {
        const next = { ...e };
        delete next[clause.id];
        return next;
      });
      setLastResults((r) => {
        const next = { ...r };
        delete next[clause.id];
        return next;
      });
      await load();
    });

  const acceptClaim = (claimNumber: number, rows: SpecClaimClause[]) =>
    run(`claim-accept-${claimNumber}`, async () => {
      const payload = rows
        .filter((c) => c.proposed || lastResults[c.id])
        .map((c) => ({
          clause_index: c.clause_index,
          ...acceptPayload(c.proposed || lastResults[c.id], edits[c.id]),
        }));
      await postSpecClaimAccept(matterId, claimNumber, payload);
      setEdits((e) => {
        const next = { ...e };
        for (const c of rows) delete next[c.id];
        return next;
      });
      setLastResults((r) => {
        const next = { ...r };
        for (const c of rows) delete next[c.id];
        return next;
      });
      setAcceptedNote(
        `Claim ${claimNumber} is now in the Detailed Description `
        + `(${payload.length} clause${payload.length === 1 ? '' : 's'}).`,
      );
      broadcastAccepted(claimNumber);
      await load();
    });

  /** The specification as it stands, as a DOCX — the same file the
   * Spec page's export produces, behind the same pre-export check.
   * Asked for by the drafting team so the enabled claims can be taken
   * out of here once they are accepted. A blocking preflight finding
   * refuses the export; advisories (the IDF gap among them) and the
   * warnings the export itself reports are shown here, since this
   * path does not go through the Spec page's export dialog. */
  const exportSpecification = () =>
    run('export', async () => {
      setExportNote(null);
      const preflight = await getSpecExportPreflight(matterId);
      const errors = preflight.warnings.filter((w) => w.severity === 'error');
      if (preflight.blocking || errors.length > 0) {
        setErr({
          kind: 'validation', retryable: false, code: 'export_blocked',
          message: 'The specification cannot be exported yet: '
            + errors.map((w) => w.message).join(' ')
            + ' Fix this on the Spec page and export again.',
        });
        return;
      }
      const { blob, filename, warnings } = await downloadSpecDocx(matterId, {
        includeFigures: true, includeProvenance: false,
      });
      // A locked specification (drawings un-frozen elsewhere) comes
      // back as a 200 JSON envelope, not a document. Saving that as
      // spec.docx would be a broken file with no explanation.
      if ((blob.type || '').includes('json')) {
        let reason = 'the specification is locked';
        try {
          const parsed = JSON.parse(await readBlobText(blob));
          if (parsed?.locked_reason) reason = String(parsed.locked_reason).replace(/_/g, ' ');
        } catch { /* keep the generic reason */ }
        setErr({
          kind: 'conflict', retryable: false, code: 'spec_locked',
          message: `Nothing was exported — ${reason}.`,
        });
        return;
      }
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
      const advisories = preflight.warnings
        .filter((w) => w.severity !== 'error')
        .map((w) => w.message);
      const post = warnings.map(humaniseExportWarning);
      const lines = Array.from(new Set([...advisories, ...post]));
      setExportNote(
        lines.length
          ? `Downloaded ${filename}. Review before filing: ${lines.join(' ')}`
          : `Downloaded ${filename}.`,
      );
    });

  const discardClaim = (claimNumber: number, rows: SpecClaimClause[]) =>
    run(`claim-discard-${claimNumber}`, async () => {
      await deleteSpecClaimProposals(matterId, claimNumber);
      setEdits((e) => {
        const next = { ...e };
        for (const c of rows) delete next[c.id];
        return next;
      });
      setLastResults((r) => {
        const next = { ...r };
        for (const c of rows) delete next[c.id];
        return next;
      });
      await load();
    });

  const enableClaim = (claimNumber: number) =>
    run(`claim-${claimNumber}`, async () => {
      // Propose the whole claim in one run: reviewing clause by clause
      // meant a round trip of a minute or more per clause.
      const res = await postSpecClaimEnable(matterId, claimNumber, true);
      if ('job_id' in res) {
        setProgress(`Writing claim ${claimNumber}…`);
        const out = await awaitEnableJob(res.job_id);
        await load();
        const outcome = describeEnableAllResult(out);
        if (outcome) setErr(outcome);
        return;
      }
      if ((res as { status?: string }).status === 'running') {
        // The server took the work. Mark it running now so the poll
        // starts even before the first clause is written.
        setGenerating((g) => (g.includes(claimNumber) ? g : [...g, claimNumber]));
        return;
      }
      await load();
    });

  const enableAll = () =>
    run('all', async () => {
      const pendingClaims = claimNumbers.filter(
        (n) => claimStatus(byClaim.get(n) || []) !== 'done',
      );
      if (pendingClaims.length === 0) return;
      setProgress('Starting…');
      // ONE request that starts a server-side job, instead of the
      // old client-side loop of one request per claim. Each claim is
      // up to two 8k-token LLM calls per clause; on a thinking model
      // that outlives the ~100s proxy timeout, and the loop died
      // with no JSON body and no way to tell how far it had got.
      const started = await postSpecEnableAllClaims(
        matterId, pendingClaims, true,
      );
      if ((started as { status?: string }).status === 'running') {
        const claims = (started as { claims_started?: number[] })
          .claims_started || pendingClaims;
        setGenerating((g) => Array.from(new Set([...g, ...claims])));
        return;
      }
      if (!('job_id' in started)) {
        // Async path off (local dev): the work already ran inline.
        await load();
        const outcome = describeEnableAllResult(started);
        if (outcome) setErr(outcome);
        return;
      }
      // Poll until the job settles. The beacon carries the claim
      // currently running, so progress stays honest instead of an
      // indeterminate spinner.
      const out = await awaitEnableJob(started.job_id);
      await load();
      const outcome = describeEnableAllResult(out);
      if (outcome) setErr(outcome);
    });

  /** QC grades the text as it stands in the fields, edits included,
   * so a finding can be fixed here and the QC run again to clear it. */
  const qcClaim = (claimNumber: number, rows: SpecClaimClause[]) =>
    run(`qc-${claimNumber}`, async () => {
      const payload = rows
        .filter((c) => c.proposed || lastResults[c.id])
        .map((c) => ({
          clause_index: c.clause_index,
          ...acceptPayload(c.proposed || lastResults[c.id], edits[c.id]),
        }));
      const res = await postSpecClaimQc(matterId, claimNumber, payload);
      setQcByClaim((q) => ({
        ...q,
        [claimNumber]: { findings: res.findings, summary: res.summary },
      }));
    });

  const mergeClause = (clause: SpecClaimClause) =>
    run(`clause-${clause.id}`, async () => {
      await postSpecClauseMerge(
        matterId, clause.claim_number, clause.clause_index,
      );
      await load();
    });

  const splitClause = (clause: SpecClaimClause) => {
    const sel = window.getSelection();
    const host = clauseRefs.current[clause.id];
    if (!sel || !host || !sel.anchorNode || !host.contains(sel.anchorNode)) {
      setErr({
        message: 'Click a position inside the clause text first, then Split.',
        kind: 'validation', retryable: false,
      });
      return;
    }
    const offset = sel.anchorOffset;
    if (offset <= 0 || offset >= clause.clause_text.length) {
      setErr({
        message: 'Place the cursor strictly inside the clause text to split.',
        kind: 'validation', retryable: false,
      });
      return;
    }
    run(`clause-${clause.id}`, async () => {
      await postSpecClauseSplit(
        matterId, clause.claim_number, clause.clause_index, offset,
      );
      await load();
    });
  };

  const resplit = (claimNumber: number, useLlm: boolean) => {
    if (!window.confirm(
      `Re-split claim ${claimNumber}? Manual clause edits are discarded `
      + 'and its enabled paragraphs will need regeneration.',
    )) return;
    run(`claim-${claimNumber}`, async () => {
      const res = await postSpecClausesResplit(matterId, claimNumber, useLlm);
      if (res.llm_split_rejected) {
        setErr({
          kind: 'validation', retryable: false,
          message: 'AI split was rejected (it reworded the claim) — kept '
            + 'the deterministic segmentation.',
        });
      }
      await load();
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 text-sm text-zinc-500">
        Loading claim clauses…
      </div>
    );
  }

  return (
    <div data-testid="description-workbench" className="max-w-6xl mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              aria-label="Back"
              className="text-zinc-500 hover:text-zinc-900"
            >
              ←
            </button>
          )}
          <h1
            className="text-2xl font-semibold text-zinc-900"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Description Workbench
          </h1>
          <span className="text-[11px] text-zinc-500">
            claim-by-claim specification drafting
          </span>
        </div>
        <div className="flex items-center gap-2">
          <CoursesLink onOpenCourses={onOpenCourses} testId="dd-courses-button" />
          <button
            type="button"
            data-testid="dd-export-spec"
            className={outlineBtn}
            disabled={busy !== null || !anyAccepted}
            onClick={exportSpecification}
            title={anyAccepted
              ? 'Download the specification as a DOCX, with every accepted claim in the Detailed Description'
              : 'Accept at least one claim first — the export contains what is in the Detailed Description'}
          >
            {busy === 'export' ? 'Exporting…' : 'Export specification'}
          </button>
          <button
            type="button"
            data-testid="dd-enable-all"
            className={primaryBtn}
            disabled={busy !== null || claimNumbers.length === 0}
            onClick={enableAll}
            title="Enable every pending clause of every claim, claim by claim"
          >
            {generating.length > 0
              ? `Writing ${generating.length} claim${generating.length === 1 ? '' : 's'}…`
              : busy === 'all'
                ? (progress || 'Starting…')
                : 'Generate all claims'}
          </button>
        </div>
      </div>

      {exportNote && (
        <div
          className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-800 flex items-start justify-between gap-3"
          role="status"
          data-testid="dd-export-note"
        >
          <div>{exportNote}</div>
          <button
            type="button"
            className="text-zinc-500 hover:text-zinc-900"
            onClick={() => setExportNote(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {acceptedNote && (
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start justify-between gap-3"
          role="status"
          data-testid="dd-accepted-note"
        >
          <div>
            <span className="font-medium">{acceptedNote}</span>{' '}
            <span className="text-emerald-800">
              Read or edit it on the Spec page under Detailed Description —
              that tab refreshes on its own when you switch to it — or use
              Export specification here.
            </span>
          </div>
          <button
            type="button"
            className="text-emerald-700 hover:text-emerald-900"
            onClick={() => setAcceptedNote(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {err && (
        <div
          className={
            err.kind === 'cost_cap'
              ? 'rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'
              : 'rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800'
          }
          role="alert"
          data-testid="dd-error"
          data-error-kind={err.kind}
          data-error-code={err.code || ''}
        >
          <div className="font-medium">{ERROR_HEADLINE[err.kind]}</div>
          <div className="mt-0.5">{err.message}</div>
          {(err.code || err.status) && (
            // Machine-readable tail: what a drafter pastes into a
            // support message, and what makes a cap distinguishable
            // from an outage at a glance.
            <div className="mt-1 text-xs opacity-70">
              {[err.code, err.status ? `HTTP ${err.status}` : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
          {err.retryable && (
            <div className="mt-1 text-xs opacity-80">
              This one is worth retrying.
            </div>
          )}
        </div>
      )}
      {resplitNote.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Claim{resplitNote.length === 1 ? '' : 's'} {resplitNote.join(', ')}{' '}
          {resplitNote.length === 1 ? 'was' : 'were'} amended since the last
          segmentation — clauses were re-split and need re-enabling.
        </div>
      )}

      <div className="grid grid-cols-[160px_1fr] gap-4">
        {/* Claim rail */}
        <nav className="space-y-1 text-sm" data-testid="dd-claim-rail">
          {claimNumbers.map((n) => {
            const st = claimStatus(byClaim.get(n) || []);
            const qc = qcByClaim[n];
            return (
              <button
                key={n}
                type="button"
                onClick={() => setActiveClaim(n)}
                className={
                  'w-full text-left px-2 py-1.5 rounded border ' +
                  (activeClaim === n
                    ? 'border-zinc-400 bg-white'
                    : 'border-transparent hover:bg-zinc-100')
                }
              >
                <span className="font-medium">Claim {n}</span>
                <span
                  className={
                    'ml-2 inline-block w-2 h-2 rounded-full ' +
                    (st === 'done' ? 'bg-emerald-500'
                      : st === 'partial' ? 'bg-amber-400' : 'bg-zinc-300')
                  }
                  title={st === 'done' ? 'All clauses enabled'
                    : st === 'partial' ? 'Partially enabled' : 'Not enabled'}
                />
                {qc && (
                  <span
                    className={
                      'ml-1 text-[10px] ' +
                      (qc.summary.blockers > 0 ? 'text-red-600'
                        : qc.summary.warnings > 0 ? 'text-amber-600'
                          : 'text-emerald-600')
                    }
                  >
                    QC {qc.summary.blockers > 0
                      ? `✗${qc.summary.blockers}`
                      : qc.summary.warnings > 0
                        ? `⚠${qc.summary.warnings}` : '✓'}
                  </span>
                )}
              </button>
            );
          })}
          {claimNumbers.length === 0 && (
            <div className="text-xs text-zinc-500 p-2">
              No frozen claims — freeze Stage 3 first.
            </div>
          )}
        </nav>

        {/* Clause cards */}
        <div className="space-y-6 min-w-0">
          {claimNumbers
            .filter((n) => activeClaim === null || activeClaim === n)
            .map((n) => {
              const rows = byClaim.get(n) || [];
              const qc = qcByClaim[n];
              const st = claimStatus(rows);
              const hasProposal = rows.some((c) => c.proposed || lastResults[c.id]);
              return (
                <section
                  key={n}
                  className="border border-zinc-200 rounded-md bg-white"
                  data-testid={`dd-claim-${n}`}
                >
                  <header className="flex items-center justify-between border-b border-zinc-100 px-4 py-2">
                    <h2 className="text-sm font-semibold text-zinc-900">
                      Claim {n}
                      <span className="ml-2 text-[11px] font-normal text-zinc-400">
                        {rows.length} clause{rows.length === 1 ? '' : 's'}
                      </span>
                    </h2>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className={outlineBtn}
                        disabled={busy !== null}
                        onClick={() => resplit(n, false)}
                        title="Discard manual clause edits and re-run the deterministic segmentation"
                      >
                        Re-split
                      </button>
                      <button
                        type="button"
                        className={outlineBtn}
                        disabled={busy !== null}
                        onClick={() => resplit(n, true)}
                        title="Re-segment with AI assist (accepted only when it reproduces the claim verbatim)"
                      >
                        Re-split (AI)
                      </button>
                      <button
                        type="button"
                        data-testid={`dd-enable-claim-${n}`}
                        className={primaryBtn}
                        disabled={
                          busy !== null || st === 'done' || generating.includes(n)
                        }
                        onClick={() => enableClaim(n)}
                        title="Write this claim's specification text for review; nothing is added to the document until you accept"
                      >
                        {generating.includes(n)
                          ? `Writing ${rows.filter((c) => c.proposed).length}/${rows.length}…`
                          : busy === `claim-${n}`
                            ? 'Starting…'
                            : 'Generate specification'}
                      </button>
                      {/* QC sits BEFORE Accept and grades the proposed
                          text, so a blocker is found while the text is
                          still editable here rather than after it is
                          in the document. */}
                      <button
                        type="button"
                        data-testid={`dd-qc-claim-${n}`}
                        className={outlineBtn}
                        disabled={busy !== null || !hasProposal && st === 'none'}
                        onClick={() => qcClaim(n, rows)}
                        title={hasProposal
                          ? "QC this claim's proposed text before accepting it"
                          : "QC this claim's accepted description blocks"}
                      >
                        {busy === `qc-${n}` ? 'QC…' : 'QC claim'}
                      </button>
                      {hasProposal && (
                        <>
                          <button
                            type="button"
                            data-testid={`dd-accept-claim-${n}`}
                            className={primaryBtn}
                            disabled={busy !== null}
                            onClick={() => acceptClaim(n, rows)}
                            title="Put this claim's reviewed text into the Detailed Description"
                          >
                            {busy === `claim-accept-${n}`
                              ? 'Accepting…' : '✓ Accept claim'}
                          </button>
                          <button
                            type="button"
                            data-testid={`dd-discard-claim-${n}`}
                            className={outlineBtn}
                            disabled={busy !== null}
                            onClick={() => discardClaim(n, rows)}
                            title="Throw away every proposal on this claim"
                          >
                            {busy === `claim-discard-${n}`
                              ? 'Discarding…' : 'Discard claim'}
                          </button>
                        </>
                      )}
                    </div>
                  </header>

                  <div className="divide-y divide-zinc-100">
                    {rows.map((clause) => {
                      const anchored = anchoredTermsFor(n, clause.clause_index);
                      const last = lastResults[clause.id];
                      const linked = clause.linked;
                      // Generated but not yet in the description. The
                      // server copy wins over the local one so a reload
                      // (or another tab) still shows what is waiting.
                      const proposed = clause.proposed
                        || (clause.status !== 'enabled' ? last : undefined);
                      return (
                        <div key={clause.id} className="px-4 py-3 space-y-2">
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">
                              C{clause.clause_index + 1}
                              {clause.origin === 'manual' && ' ✎'}
                            </span>
                            <p
                              ref={(el) => {
                                clauseRefs.current[clause.id] = el;
                              }}
                              className={
                                'text-sm leading-6 text-zinc-800 select-text ' +
                                (clause.status === 'enabled'
                                  ? '' : 'text-zinc-700')
                              }
                              data-testid={`dd-clause-${n}-${clause.clause_index}`}
                            >
                              {clause.clause_text}
                            </p>
                          </div>

                          {/* Selection chips + actions */}
                          <div className="flex flex-wrap items-center gap-1.5 pl-8">
                            {/* Every chip is a server-side anchor: a
                                selection is saved the moment it is made
                                and defined by whichever button generates
                                the claim. ✓ marks a term that already has
                                its definition in the description. */}
                            {anchored.map((t) => (
                              <span
                                key={t.id}
                                data-testid={`dd-term-${n}-${clause.clause_index}-${t.term_text}`}
                                className={
                                  'text-[11px] px-2 py-0.5 rounded-full border ' +
                                  (t.paragraph_id
                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                    : 'bg-sky-50 border-sky-200 text-sky-800')
                                }
                                title={t.paragraph_id
                                  ? `${t.term_kind} — defined in the description`
                                  : `${t.term_kind} — will be defined when the claim is generated`}
                              >
                                {t.term_text}
                                {t.paragraph_id ? ' ✓' : ''}
                                {!t.paragraph_id && (
                                  <button
                                    type="button"
                                    className="ml-1 text-sky-600 hover:text-sky-900"
                                    disabled={busy !== null}
                                    onClick={() => removeSelection(clause, t)}
                                    aria-label={`Remove ${t.term_text}`}
                                    title="Remove — it will not be defined"
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            ))}
                            <button
                              type="button"
                              className={outlineBtn}
                              disabled={busy !== null}
                              onClick={() => addSelection(clause, 'term')}
                              title="Add the highlighted text as a Term to define"
                            >
                              + Term
                            </button>
                            <button
                              type="button"
                              className={outlineBtn}
                              disabled={busy !== null}
                              onClick={() => addSelection(clause, 'phrase')}
                              title="Add the highlighted text as a Phrase to define"
                            >
                              + Phrase
                            </button>
                            {/* No per-clause generate button. Writing one
                                clause at a time meant a round trip of
                                minutes each; the claim is the unit of
                                work now. Split and merge here, then
                                generate the claim from its header. */}
                            {proposed && (
                              <>
                                <button
                                  type="button"
                                  data-testid={`dd-accept-${n}-${clause.clause_index}`}
                                  className={primaryBtn}
                                  disabled={busy !== null}
                                  onClick={() => acceptClause(clause)}
                                  title="Put this text into the Detailed Description"
                                >
                                  ✓ Accept
                                </button>
                                <button
                                  type="button"
                                  data-testid={`dd-discard-${n}-${clause.clause_index}`}
                                  className={outlineBtn}
                                  disabled={busy !== null}
                                  onClick={() => discardProposal(clause)}
                                  title="Throw this text away — nothing is written"
                                >
                                  Discard
                                </button>
                              </>
                            )}
                            {clause.clause_index > 0 && (
                              <button
                                type="button"
                                className={outlineBtn}
                                disabled={busy !== null}
                                onClick={() => mergeClause(clause)}
                                title="Merge this clause into the previous one"
                              >
                                ⤴ Merge
                              </button>
                            )}
                            <button
                              type="button"
                              className={outlineBtn}
                              disabled={busy !== null}
                              onClick={() => splitClause(clause)}
                              title="Split this clause at the cursor position"
                            >
                              ✂ Split
                            </button>
                          </div>

                          {/* Proposed — read it here; nothing is in the
                              Detailed Description until Accept. */}
                          {proposed && (
                            <div
                              className="pl-8 space-y-2"
                              data-testid={`dd-proposed-${n}-${clause.clause_index}`}
                            >
                              <p className="text-[11px] font-medium uppercase tracking-wide text-amber-700">
                                Waiting for you — edit if you like, nothing is
                                in the description until you accept
                              </p>
                              {/* Block order is the agreed one: the clause
                                  restated, the definitions, the
                                  explanation, then the claim benefit. */}
                              {typeof proposed.clause_restatement === 'string'
                                && proposed.clause_restatement && (
                                <label className="block">
                                  <span className="text-[11px] font-medium text-zinc-500">
                                    Clause, restated
                                  </span>
                                  <textarea
                                    className="mt-0.5 w-full rounded border border-amber-200 bg-white px-2 py-1 text-[13px] leading-5 text-zinc-700"
                                    rows={2}
                                    data-testid={`dd-proposed-restatement-${n}-${clause.clause_index}`}
                                    value={
                                      edits[clause.id]?.clause_restatement
                                      ?? proposed.clause_restatement
                                    }
                                    onChange={(e) => setEdits((prev) => ({
                                      ...prev,
                                      [clause.id]: {
                                        ...prev[clause.id],
                                        clause_restatement: e.target.value,
                                      },
                                    }))}
                                  />
                                </label>
                              )}
                              {(proposed.definitions || []).map((d) => (
                                <label key={d.term} className="block">
                                  <span className="text-[11px] font-medium text-zinc-500">
                                    {d.term}
                                  </span>
                                  <textarea
                                    className="mt-0.5 w-full rounded border border-amber-200 bg-white px-2 py-1 text-[13px] leading-5 text-zinc-700"
                                    rows={3}
                                    data-testid={`dd-proposed-def-${n}-${clause.clause_index}-${d.term}`}
                                    value={
                                      edits[clause.id]?.definitions?.[d.term]
                                      ?? d.paragraph
                                    }
                                    onChange={(e) => setEdits((prev) => ({
                                      ...prev,
                                      [clause.id]: {
                                        ...prev[clause.id],
                                        definitions: {
                                          ...(prev[clause.id]?.definitions || {}),
                                          [d.term]: e.target.value,
                                        },
                                      },
                                    }))}
                                  />
                                </label>
                              ))}
                              {proposed.clause_paragraph && (
                                <textarea
                                  className="w-full rounded border border-amber-300 bg-white px-2 py-1 text-[13px] leading-5 text-zinc-800"
                                  rows={8}
                                  data-testid={`dd-proposed-text-${n}-${clause.clause_index}`}
                                  value={
                                    edits[clause.id]?.clause_paragraph
                                    ?? proposed.clause_paragraph
                                  }
                                  onChange={(e) => setEdits((prev) => ({
                                    ...prev,
                                    [clause.id]: {
                                      ...prev[clause.id],
                                      clause_paragraph: e.target.value,
                                    },
                                  }))}
                                />
                              )}
                              {proposed.claim_benefit_paragraph && (
                                <textarea
                                  className="w-full rounded border border-amber-400 bg-white px-2 py-1 text-[13px] leading-5 text-amber-900 italic"
                                  rows={3}
                                  data-testid={`dd-proposed-benefit-${n}-${clause.clause_index}`}
                                  value={
                                    edits[clause.id]?.claim_benefit_paragraph
                                    ?? proposed.claim_benefit_paragraph
                                  }
                                  onChange={(e) => setEdits((prev) => ({
                                    ...prev,
                                    [clause.id]: {
                                      ...prev[clause.id],
                                      claim_benefit_paragraph: e.target.value,
                                    },
                                  }))}
                                />
                              )}
                              {/* Anything the writer could not ground in
                                  the disclosure, including a reference
                                  sign that is not in the registry. Read
                                  before accepting. */}
                              {(proposed.flags || []).map((f, i) => (
                                <div
                                  key={`pf-${i}`}
                                  data-testid={`dd-proposed-flag-${n}-${clause.clause_index}-${i}`}
                                  className="text-[12px] text-amber-900 bg-amber-100 border border-amber-300 rounded px-2 py-1"
                                >
                                  <span className="font-medium">{f.issue}: </span>
                                  {f.detail}
                                </div>
                              ))}
                              {(proposed.definition_gaps || []).map((g) => (
                                <div
                                  key={`pg-${g.term}`}
                                  className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                                >
                                  “{g.term}” not defined: {g.reason}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Generated output */}
                          {(linked?.definitions?.length || linked?.clause_paragraph
                            || linked?.restatement || last) && (
                            <div className="pl-8 space-y-2">
                              {linked?.restatement && (
                                <p className="text-[13px] leading-5 text-zinc-700 border-l-2 border-zinc-300 pl-3">
                                  {linked.restatement.body_text}
                                </p>
                              )}
                              {(linked?.definitions || []).map((d) => (
                                <p
                                  key={d.id}
                                  className="text-[13px] leading-5 text-zinc-600 border-l-2 border-zinc-200 pl-3"
                                >
                                  {d.body_text}
                                </p>
                              ))}
                              {linked?.clause_paragraph && (
                                <p className="text-[13px] leading-5 text-zinc-800 border-l-2 border-emerald-300 pl-3">
                                  {linked.clause_paragraph.body_text}
                                </p>
                              )}
                              {linked?.benefit_paragraph && (
                                <p className="text-[13px] leading-5 text-emerald-900 border-l-2 border-emerald-500 pl-3 italic">
                                  {linked.benefit_paragraph.body_text}
                                </p>
                              )}
                              {(last?.definition_gaps || []).map((g) => (
                                <div
                                  key={g.term}
                                  className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                                >
                                  “{g.term}” not defined: {g.reason}
                                </div>
                              ))}
                              {(last?.flags || []).map((f, i) => (
                                <div
                                  key={i}
                                  className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                                >
                                  {f.issue}: {f.detail}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Per-claim QC findings */}
                  {qc && (
                    <footer
                      className="border-t border-zinc-100 px-4 py-3 space-y-1.5"
                      data-testid={`dd-qc-findings-${n}`}
                    >
                      <div className="text-xs font-medium text-zinc-500">
                        QC — {qc.summary.pass
                          ? 'no findings'
                          : `${qc.summary.blockers} blocker(s), `
                            + `${qc.summary.warnings} warning(s), `
                            + `${qc.summary.info} info`}
                      </div>
                      {qc.findings.map((f, i) => (
                        <div
                          key={i}
                          className={
                            'text-[12px] border rounded px-2 py-1.5 ' +
                            (SEVERITY_CLASS[f.severity] || SEVERITY_CLASS.info)
                          }
                        >
                          <span className="font-medium">
                            [{f.category}
                            {f.clause_index !== null
                              ? ` · C${f.clause_index + 1}` : ''}]
                          </span>{' '}
                          {f.message}
                          {f.suggested_fix && (
                            <div className="mt-0.5 text-[11px] opacity-80">
                              Fix: {f.suggested_fix}
                            </div>
                          )}
                        </div>
                      ))}
                    </footer>
                  )}
                </section>
              );
            })}
        </div>
      </div>

      <div className="text-xs text-zinc-500 border border-zinc-200 bg-zinc-50 rounded p-3">
        <strong>How it works:</strong> get the clauses right first — split,
        merge, and select the Terms or Phrases you want defined (each one
        is saved as you add it). Then <em>Generate specification</em> on
        the claim (or <em>Generate all claims</em>). Each clause comes back
        as: the clause restated, one definition per selected term, the
        explanation of the clause, and the claim benefit on the last
        clause. The claim is written server-side and each clause appears
        here as it is finished, so you can close the tab and come back.
        Run <em>QC claim</em> on the proposed text, edit anything in place,
        then <em>Accept claim</em> to put it into the Detailed Description,
        or <em>Discard claim</em> to throw it away. Accepted paragraphs
        stay editable on the Spec page like any other paragraph, and
        <em> Export specification</em> downloads the document.
      </div>
      <AppFooter />
    </div>
  );
}
